<!--
  * Path: packages/frontend/src/core/components/forms/Multiselect.vue
  * Task: Refactored Multiselect to match the UX of the global EntitySelect (Sticky Search, Select All).
-->
<template>
  <div class="d-flex align-center mb-6">
    <v-select
      :model-value="modelValueProxy"
      :label="props.field.name"
      :items="filteredItems"
      item-title="title"
      item-value="id"
      variant="outlined"
      density="compact"
      multiple
      chips
      closable-chips
      hide-details
      class="flex-grow-1"
      :disabled="props.field.readonly"
      @update:menu="onMenuUpdate"
      @update:model-value="handleUpdate"
    >
      <!-- PREPEND ITEM SLOT: Search & Select All -->
      <template v-slot:prepend-item>
        <!-- Sticky Search Bar -->
        <div class="sticky-search px-3 pt-2 pb-2 bg-surface">
          <v-text-field
            ref="filterInputRef"
            v-model="searchQuery"
            placeholder="Filter entries..."
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @click.stop
            @keydown.stop
          ></v-text-field>
        </div>

        <!-- Select All / Deselect All Button -->
        <v-list-item @click="toggleSelectAll" class="mt-1" density="compact">
          <template v-slot:prepend>
            <div class="d-flex align-center justify-center me-3" style="width: 24px;">
              <v-icon size="small" :color="isAllSelected ? 'primary' : isSomeSelected ? 'primary' : 'medium-emphasis'">
                {{ isAllSelected ? 'mdi-checkbox-marked' : isSomeSelected ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </div>
          </template>
          <v-list-item-title class="font-weight-bold">
            {{ isAllSelected ? 'Uncheck All' : 'Select All' }}
          </v-list-item-title>
        </v-list-item>
        <v-divider class="mb-1"></v-divider>
      </template>

      <!-- Dropdown List Item -->
      <template v-slot:item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="item.raw.title" density="compact">
          <template v-slot:prepend>
            <div class="d-flex align-center justify-center me-3" style="width: 24px;">
              <v-icon size="small" :color="isItemSelected(item) ? 'primary' : 'medium-emphasis'">
                {{ isItemSelected(item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </div>
            <v-avatar v-if="!props.field.hideIcon" :color="getChipStyle(item).color" size="24" class="me-3 rounded">
              <v-icon color="white" size="14">{{ getChipStyle(item).icon || 'mdi-shape' }}</v-icon>
            </v-avatar>
          </template>
        </v-list-item>
      </template>

      <!-- Selected Item Display (Chips) -->
      <template v-slot:chip="{ props: chipProps, item }">
        <v-chip
          v-bind="chipProps"
          :style="{ backgroundColor: getChipStyle(item).color || 'var(--v-theme-primary)' }"
          variant="flat"
          size="small"
          class="text-white font-weight-bold mt-1 mb-1"
          closable
          @click:close="removeItem(item)"
        >
          <template v-slot:prepend>
            <v-icon v-if="!props.field.hideIcon" color="white" size="14" class="me-1" style="margin-left: -4px;">
              {{ getChipStyle(item).icon || 'mdi-shape' }}
            </v-icon>
          </template>
          {{ item.raw?.title || item.title }}
        </v-chip>
      </template>
    </v-select>

    <v-tooltip v-if="props.field.info" location="end">
      <template v-slot:activator="{ props: tooltipProps }">
        <v-btn v-bind="tooltipProps" icon="mdi-information-outline" variant="text" size="x-small" class="ms-1 flex-grow-0" @click="$emit('show-info')"></v-btn>
      </template>
      <span>{{ props.field.info }}</span>
    </v-tooltip>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useAppStyle } from '@/core/composables/useAppStyle';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';

export interface FieldProps {
  id: string;
  name?: string;
  info?: string;
  entitySource?: string;
  readonly?: boolean;
  hideIcon?: boolean;
}

const props = defineProps<{ field: FieldProps }>();
const modelValue = defineModel<any[]>({ default: undefined });
const emit = defineEmits<{ (e: 'show-info'): void; }>();

const workspaceStore = useWorkspaceStore();
const { getEntityStyle, resolveHexColor } = useAppStyle();
const { getSelectOptions } = useSelectOptions();
const formContext = inject(FormContextKey, null);

const searchQuery = ref<string>('');
const filterInputRef = ref<any>(null);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const getNested = (obj: any, path: string) => path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
const setNested = (obj: any, path: string, value: any) => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (keys[i] !== undefined) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
  }
  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) current[lastKey] = value;
};

// Use provided items via entitySource mapping
const baseItems = computed(() => getSelectOptions(props.field.entitySource || '', props.field.id) || []);

// Custom proxy mediating between Vue's v-model and the direct Entity mutations
const modelValueProxy = computed<any[]>({
  get: () => {
    if (modelValue.value !== undefined) return modelValue.value;
    if (!activeEntity.value || !props.field.id) return [];
    let val = props.field.id.includes('.') ? getNested(activeEntity.value, props.field.id) : (activeEntity.value as any)[props.field.id];

    if (props.field.id === 'week') {
      if (!Array.isArray(val)) {
        if (typeof val === 'string' && val.includes(',')) val = val.split(',').map(s => parseInt(s.trim(), 10));
        else if (val !== undefined && val !== null && val !== '') val = [parseInt(String(val), 10)];
        else val = [];
      } else {
        val = val.map((v: string | number) => parseInt(String(v), 10)).filter((n: number) => !isNaN(n));
      }
    }
    return val || [];
  },
  set: () => {} // Managed by handleUpdate explicitly
});

// Explicit handler for ALL updates from Vuetify
const handleUpdate = (val: any) => {
  let processedVal = Array.isArray(val) ? val : (val ? [val] : []);
  if (props.field.id === 'week') {
    processedVal = processedVal.map(v => parseInt(String(v), 10)).filter(n => !isNaN(n));
  }

  if (modelValue.value !== undefined) {
    modelValue.value = processedVal;
    return;
  }

  if (!activeEntity.value || !props.field.id) return;

  if (props.field.id.includes('.')) {
    setNested(activeEntity.value, props.field.id, processedVal);
  } else {
    (activeEntity.value as any)[props.field.id] = processedVal;
  }
};

const isItemSelected = (item: any): boolean => {
  if (!modelValueProxy.value || !Array.isArray(modelValueProxy.value)) return false;
  const targetId = item.raw?.id || item.value || item.id;
  if (targetId === undefined || targetId === null) return false;

  return modelValueProxy.value.some(val => {
    if (val === undefined || val === null) return false;
    const valId = typeof val === 'object' ? (val.id !== undefined ? val.id : val.value) : val;
    return String(valId).toLowerCase() === String(targetId).toLowerCase();
  });
};

const removeItem = (item: any) => {
  const valToRemove = item.raw?.id || item.value || item.id;
  const newArray = modelValueProxy.value.filter((val: any) => {
    const valId = typeof val === 'object' ? (val.id !== undefined ? val.id : val.value) : val;
    return String(valId).toLowerCase() !== String(valToRemove).toLowerCase();
  });
  handleUpdate(newArray);
};

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return baseItems.value;
  return baseItems.value.filter((item: any) => {
    const matchesSearch = String(item.title || '').toLowerCase().includes(query);
    const isSelected = isItemSelected(item);
    return matchesSearch || isSelected;
  });
});

// --- SELECT ALL LOGIC ---
const isAllSelected = computed(() => {
  if (filteredItems.value.length === 0) return false;
  return filteredItems.value.every(item => isItemSelected(item));
});

const isSomeSelected = computed(() => {
  if (filteredItems.value.length === 0) return false;
  return filteredItems.value.some(item => isItemSelected(item));
});

const toggleSelectAll = () => {
  const currentSelected = [...modelValueProxy.value];
  const filteredValues = filteredItems.value.map((item: any) => item.id);

  if (isAllSelected.value) {
    // Deselect filtered
    const newSelected = currentSelected.filter(val => !filteredValues.includes(val));
    handleUpdate(newSelected);
  } else {
    // Select filtered
    const newSelected = new Set([...currentSelected, ...filteredValues]);
    handleUpdate(Array.from(newSelected));
  }
};

const getChipStyle = (item: any) => {
  const raw = item.raw || {};
  const typeStyle = getEntityStyle(props.field.entitySource || '');
  return {
    icon: raw.icon || typeStyle.icon || 'mdi-shape',
    color: resolveHexColor(raw.color || typeStyle.color || 'grey')
  };
};

const onMenuUpdate = (isOpen: boolean): void => {
  if (isOpen) {
    nextTick(() => { setTimeout(() => filterInputRef.value?.focus(), 150); });
  } else {
    searchQuery.value = '';
  }
};
</script>

<style scoped>
.sticky-search {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep(.v-list-item) {
  padding-inline-start: 16px !important;
}

:deep(.v-list-item__prepend) {
  margin-inline-end: 4px !important;
  display: flex !important;
  align-items: center !important;
  overflow: visible !important;
}

:deep(.v-list-item__prepend > .v-icon),
:deep(.v-list-item__prepend > .v-avatar) {
  flex-shrink: 0 !important;
}

:deep(.v-chip) {
  overflow: visible !important;
}

:deep(.v-chip__prepend) {
  margin-inline-start: 2px !important;
}
</style>
```eof
